import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { ChatHomeLayout, ChatHomeSignals } from "@/types/user-settings"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { LanguageSwitcher } from "@/components/ui/language-switcher"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Switch } from "@/components/ui/switch"
import { ThemeSwitcher } from "@/components/ui/theme-switcher"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { M365InfoModal } from "@/features/ai-explorer/components/m365-info-modal"
import { useM365ApprovalGate } from "@/features/ai-explorer/hooks/use-m365-approval-gate"
import { ContextualActionsCheckbox } from "@/features/base-settings/components/contextual-actions-checkbox"
import { FollowUpQuestionsCheckbox } from "@/features/base-settings/components/follow-up-questions-checkbox"
import { ShouldStreamCheckbox } from "@/features/base-settings/components/should-stream-checkbox"
import { StreamingCursorSelector } from "@/features/base-settings/components/streaming-cursor-selector"
import { VoiceSelector } from "@/features/base-settings/components/voice-selector"
import { useHeavyPreviewGate } from "@/features/chat/hooks/use-heavy-preview-gate"
import { PersonalSourcesEntry } from "@/features/personalization/components/personal-sources-panel"
import { PrismViewSelector } from "@/features/personalization/components/prism-view-selector"
import { useGeneralSettings } from "@/hooks/use-module-settings"

const CHAT_HOME_LAYOUT_OPTIONS: { value: ChatHomeLayout; labelKey: string; fallback: string }[] = [
  {
    value: "a1-strip",
    labelKey: "personalization.preferences.home.layouts.a1Strip",
    fallback: "Centered focus",
  },
  {
    value: "a2-rail",
    labelKey: "personalization.preferences.home.layouts.a2Rail",
    fallback: "Context rail",
  },
]

const CHAT_HOME_SIGNAL_OPTIONS: {
  key: keyof ChatHomeSignals
  labelKey: string
  fallback: string
}[] = [
  {
    key: "meetings",
    labelKey: "personalization.preferences.home.signals.meetings",
    fallback: "Meetings",
  },
  {
    key: "unreadMail",
    labelKey: "personalization.preferences.home.signals.unreadMail",
    fallback: "Unread mail",
  },
  {
    key: "recentFiles",
    labelKey: "personalization.preferences.home.signals.recentFiles",
    fallback: "Recent files",
  },
  {
    key: "trending",
    labelKey: "personalization.preferences.home.signals.trending",
    fallback: "Trending files",
  },
  {
    key: "people",
    labelKey: "personalization.preferences.home.signals.people",
    fallback: "People",
  },
  {
    key: "projects",
    labelKey: "personalization.preferences.home.signals.projects",
    fallback: "Projects",
  },
  {
    key: "memoryNudges",
    labelKey: "personalization.preferences.home.signals.memoryNudges",
    fallback: "Memory updates",
  },
]

// Day Brief sub-section toggles. These control sub-sections of the orange box,
// distinct from the snapshot tile toggles above which control the small icon
// cards. Kept in a separate group so users don't conflate "hide the Mail tile"
// with "hide the Inbox highlights inside the Day Brief" — they're independent.
const CHAT_HOME_DAY_BRIEF_SIGNAL_OPTIONS: {
  key: keyof ChatHomeSignals
  labelKey: string
  fallback: string
}[] = [
  {
    key: "dayBriefInbox",
    labelKey: "personalization.preferences.home.daybrief.inbox",
    fallback: "Inbox highlights",
  },
  {
    key: "dayBriefProjectPulse",
    labelKey: "personalization.preferences.home.daybrief.projectPulse",
    fallback: "Project pulse",
  },
  {
    key: "dayBriefWaitingOnYou",
    labelKey: "personalization.preferences.home.daybrief.waitingOnYou",
    fallback: "Waiting on you",
  },
  {
    key: "dayBriefTomorrow",
    labelKey: "personalization.preferences.home.daybrief.tomorrow",
    fallback: "Tomorrow preview",
  },
]

const DEFAULT_CHAT_HOME_SIGNALS: ChatHomeSignals = {
  meetings: true,
  unreadMail: true,
  recentFiles: true,
  trending: true,
  people: true,
  projects: true,
  memoryNudges: true,
  dayBriefInbox: true,
  dayBriefProjectPulse: true,
  dayBriefWaitingOnYou: true,
  dayBriefTomorrow: true,
}

function DocumentPreviewProtectionSettingsRow() {
  const { t } = useTranslation()
  const { heavyPreviewsGated, setManualGated } = useHeavyPreviewGate()

  function handleCheckedChange(checked: boolean | "indeterminate") {
    if (checked === "indeterminate") {
      return
    }
    setManualGated(checked)
  }

  return (
    <section className="flex items-center gap-2" data-testid="document-preview-protection-setting">
      <Checkbox
        checked={heavyPreviewsGated}
        id="document-preview-protection"
        onCheckedChange={handleCheckedChange}
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <Label className="cursor-help" htmlFor="document-preview-protection">
            {t("settingsmenu.document_preview_protection", {
              defaultValue: "Confirm before loading Word and Excel previews",
            })}
          </Label>
        </TooltipTrigger>
        <TooltipContent className="max-w-[280px]" side="top">
          <p>
            {t("settingsmenu.document_preview_protection_tooltip", {
              defaultValue:
                "When on, large Word or Excel previews show a short confirmation first on this browser. If a preview is very slow, this can turn on by itself here; turn it off if that was a mistake.",
            })}
          </p>
        </TooltipContent>
      </Tooltip>
    </section>
  )
}

function ChatHomeSettings() {
  const { t } = useTranslation()
  const { settings, updateSettings } = useGeneralSettings()
  const chatHomeLayout = settings?.chatHomeLayout ?? "a2-rail"
  const chatHomeSignals = {
    ...DEFAULT_CHAT_HOME_SIGNALS,
    ...(settings?.chatHomeSignals ?? {}),
  }

  function handleLayoutChange(value: string) {
    void updateSettings({ chatHomeLayout: value as ChatHomeLayout })
  }

  function handleSignalChange(key: keyof ChatHomeSignals, checked: boolean | "indeterminate") {
    if (checked === "indeterminate") return
    void updateSettings({
      chatHomeSignals: {
        [key]: checked,
      },
    })
  }

  return (
    <div className="space-y-4" data-testid="chat-home-settings">
      <div className="space-y-2">
        <Label className="text-sm font-medium" htmlFor="chat-home-layout">
          {t("personalization.preferences.home.layoutLabel", {
            defaultValue: "Home layout",
          })}
        </Label>
        <Select value={chatHomeLayout} onValueChange={handleLayoutChange}>
          <SelectTrigger id="chat-home-layout">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CHAT_HOME_LAYOUT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {t(option.labelKey, { defaultValue: option.fallback })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium">
          {t("personalization.preferences.home.signalsLabel", {
            defaultValue: "Snapshot signals",
          })}
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {CHAT_HOME_SIGNAL_OPTIONS.map((signal) => (
            <section key={signal.key} className="flex items-center gap-2">
              <Checkbox
                checked={chatHomeSignals[signal.key]}
                id={`chat-home-signal-${signal.key}`}
                onCheckedChange={(checked) => handleSignalChange(signal.key, checked)}
              />
              <Label htmlFor={`chat-home-signal-${signal.key}`}>
                {t(signal.labelKey, { defaultValue: signal.fallback })}
              </Label>
            </section>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium">
          {t("personalization.preferences.home.daybriefLabel", {
            defaultValue: "Day Brief sections",
          })}
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {CHAT_HOME_DAY_BRIEF_SIGNAL_OPTIONS.map((signal) => (
            <section key={signal.key} className="flex items-center gap-2">
              <Checkbox
                checked={chatHomeSignals[signal.key]}
                id={`chat-home-signal-${signal.key}`}
                onCheckedChange={(checked) => handleSignalChange(signal.key, checked)}
              />
              <Label htmlFor={`chat-home-signal-${signal.key}`}>
                {t(signal.labelKey, { defaultValue: signal.fallback })}
              </Label>
            </section>
          ))}
        </div>
      </div>
    </div>
  )
}

function formatApprovalDate(iso: string | undefined, locale: string): string {
  if (!iso) return ""
  try {
    return new Date(iso).toLocaleDateString(locale || undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  } catch {
    return ""
  }
}

function MicrosoftIntegrationSection() {
  const { t, i18n } = useTranslation()
  const {
    approveM365,
    declineM365,
    hasM365Approval,
    hasDeclinedM365,
    approvalTimestamp,
    isM365ApprovalLoading,
  } = useM365ApprovalGate()
  const [showConsentModal, setShowConsentModal] = useState(false)

  const handleSwitchChange = (next: boolean) => {
    if (next) {
      // Going off → on (or off → re-approve after decline). Re-show the
      // consent copy so the user sees what they're enabling. Modal's Approve
      // button calls approveM365(); decline path calls declineM365() and
      // leaves the switch off.
      setShowConsentModal(true)
      return
    }
    // Going on → off. Persist a declined decision directly — no new info to
    // consent to, just an opt-out.
    declineM365()
  }

  function getStateLabel(): string {
    if (hasM365Approval && approvalTimestamp) {
      return t("m365_tool.settings.allowed_since", {
        date: formatApprovalDate(approvalTimestamp, i18n.language),
        defaultValue: "Allowed since {{date}}",
      })
    }
    if (hasDeclinedM365) {
      return t("m365_tool.settings.off", { defaultValue: "Off" })
    }
    return t("m365_tool.settings.not_decided", { defaultValue: "Not configured" })
  }
  const stateLabel = getStateLabel()

  return (
    <>
      <Card className="border-muted/50 shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">
            {t("m365_tool.settings.title", { defaultValue: "Microsoft 365 data" })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <p className="text-sm">
                {t("m365_tool.settings.description", {
                  defaultValue:
                    "Allow Hello AI to read your Microsoft 365 data (emails, calendar, files, Teams) when you ask questions that need it.",
                })}
              </p>
              <p className="text-muted-foreground text-xs">{stateLabel}</p>
            </div>
            <Switch
              checked={hasM365Approval}
              disabled={isM365ApprovalLoading}
              aria-label={t("m365_tool.settings.toggle_label", {
                defaultValue: "Microsoft 365 data access",
              })}
              onCheckedChange={handleSwitchChange}
            />
          </div>
        </CardContent>
      </Card>
      <M365InfoModal
        open={showConsentModal}
        onDismiss={() => setShowConsentModal(false)}
        onApprove={() => {
          approveM365()
          setShowConsentModal(false)
        }}
        onDecline={() => {
          declineM365()
          setShowConsentModal(false)
        }}
      />
    </>
  )
}

export function GeneralPanel() {
  const { t } = useTranslation()

  return (
    <div className="space-y-6">
      <Card className="border-muted/50 shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">
            {t("pages.common.general", { defaultValue: "AI Behavior" })}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <FollowUpQuestionsCheckbox />
          <ShouldStreamCheckbox />
          <ContextualActionsCheckbox />
          <DocumentPreviewProtectionSettingsRow />
          <VoiceSelector />
        </CardContent>
      </Card>

      <MicrosoftIntegrationSection />

      <PersonalSourcesEntry />

      <Card className="border-muted/50 shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">
            {t("pages.common.appearance", { defaultValue: "Appearance" })}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label className="text-sm font-medium" htmlFor="language">
              {t("pages.common.language", { defaultValue: "Language" })}
            </Label>
            <LanguageSwitcher />
          </div>

          <Separator />

          <div className="space-y-2">
            <Label className="text-sm font-medium" htmlFor="theme">
              {t("pages.common.theme", { defaultValue: "Theme" })}
            </Label>
            <ThemeSwitcher />
          </div>

          <Separator />

          <ChatHomeSettings />

          <Separator />

          <StreamingCursorSelector />

          <PrismViewSelector />
        </CardContent>
      </Card>
    </div>
  )
}
